import React, { useEffect } from 'react';
import { Box, Card, CardContent, Grid, Typography, Chip, LinearProgress, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { People, Spa, FitnessCenter, TrendingUp, EmojiEvents, Repeat, LocalDrink, Psychology, HelpOutline, Insights, AccessTime, AttachMoney, Storage, SwapHoriz, ExpandMore, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { useThemeMode } from '../context/ThemeContext';

export default function DashboardPage() {
  const { isDark, colors } = useThemeMode();
  
  // Theme-aware colors
  const themeColors = {
    cardBg: isDark ? '#1e293b' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    textPrimary: isDark ? '#f1f5f9' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    chipBg: isDark ? 'rgba(29,202,255,0.2)' : 'rgba(29,202,255,0.1)',
    tableBg: isDark ? '#0f172a' : '#f8fafc',
    highlightBg: isDark ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
  };

  // Mock data específico para Caldea
  const caldeaMetrics = {
    activeUsers: 1247,
    weeklyVisits: 3.2,
    rewardsRedeemed: 342,
    retentionRate: 78,
    challengeParticipants: 424,
    recoveryServices: 156,
  };

  // Datos de canjes por tipo - Caldea Brand Colors
  const rewardsByType = [
    { name: 'Recovery', value: 45, color: colors.primary },
    { name: 'Wellness', value: 30, color: colors.success },
    { name: 'Emotional', value: 25, color: '#a855f7' }
  ];

  // Tendencia de frecuencia semanal
  const frequencyTrend = [
    { week: 'Sem 1', frequency: 2.8, target: 3.0 },
    { week: 'Sem 2', frequency: 3.0, target: 3.0 },
    { week: 'Sem 3', frequency: 3.1, target: 3.0 },
    { week: 'Sem 4', frequency: 3.2, target: 3.0 }
  ];

  // Canjes por día (última semana)
  const dailyRedemptions = [
    { day: 'Lun', canjes: 42, sessions: 180 },
    { day: 'Mar', canjes: 38, sessions: 165 },
    { day: 'Mié', canjes: 55, sessions: 210 },
    { day: 'Jue', canjes: 48, sessions: 195 },
    { day: 'Vie', canjes: 62, sessions: 240 },
    { day: 'Sáb', canjes: 78, sessions: 320 },
    { day: 'Dom', canjes: 45, sessions: 185 }
  ];

  // Cohorte de retención
  const retentionData = [
    { cohort: 'Sin PEpL', d7: 65, d14: 52, d30: 41 },
    { cohort: 'Con PEpL', d7: 82, d14: 71, d30: 63 }
  ];

  // Actividad reciente
  const recentActivity = [
    { user: 'Maria G.', action: 'Canjeó Sesión Descontracturante', trigger: '5.2km cardio', time: 'Hace 12 min' },
    { user: 'Carlos P.', action: 'Completó Challenge Semanal', trigger: '4 sesiones', time: 'Hace 28 min' },
    { user: 'Ana L.', action: 'Canjeó Breathwork', trigger: 'HRV bajo', time: 'Hace 45 min' },
    { user: 'Pedro M.', action: 'Desbloqueó Pack Recovery', trigger: '12 sesiones/mes', time: 'Hace 1h' }
  ];

  const biKpis = [
    {
      icon: AttachMoney,
      title: 'Ingresos por reward',
      value: '€18.6k',
      subtitle: '↑ 8.2% vs mes anterior',
      color: colors.success,
      trend: 'up',
      info: 'Ingreso neto atribuible a rewards. Técnica: atribución post‑reward (ventana 7 días) + control holdout.'
    },
    {
      icon: AccessTime,
      title: 'Lift SmartTiming™',
      value: '+12.1%',
      subtitle: 'Apertura y redención',
      color: colors.primary,
      trend: 'up',
      info: 'Incremento vs envío estándar. Técnica: A/B multivariado por segmento y horario óptimo.'
    },
    {
      icon: Storage,
      title: 'Cost‑to‑Serve',
      value: '€7.9',
      subtitle: 'por servicio',
      color: '#a855f7',
      info: 'Costo total por servicio. Técnica: costos directos + variables por reward (sin mantenimiento/RRHH en esta versión).'
    },
    {
      icon: Spa,
      title: 'Uso de activos',
      value: '84%',
      subtitle: 'ocupación promedio',
      color: '#14b8a6',
      info: 'Utilización de activos críticos. Técnica: ocupación / capacidad por franja horaria.'
    },
    {
      icon: People,
      title: 'Cohorte Retención M3',
      value: '82%',
      subtitle: '+9 pp con PEpL',
      color: colors.warning,
      trend: 'up',
      info: 'Retención por cohortes. Técnica: seguimiento nominal por mes 3 con baseline sin PEpL.'
    },
    {
      icon: SwapHoriz,
      title: 'Cross‑sell & Partner ROI',
      value: '17.4%',
      subtitle: 'lift promedio',
      color: colors.info,
      trend: 'up',
      info: 'Impacto en ticket y recurrencia por alianza. Técnica: uplift vs control por partner.'
    },
    {
      icon: Psychology,
      title: 'NPS por franja',
      value: '74',
      subtitle: 'mejor franja 18–20h',
      color: colors.error,
      info: 'Satisfacción por horario. Técnica: NPS segmentado por franja y volumen de afluencia.'
    },
    {
      icon: Insights,
      title: 'SLA internos',
      value: '99.2%',
      subtitle: 'uptime servicios',
      color: themeColors.textSecondary,
      info: 'Disponibilidad de sistemas críticos. Técnica: uptime mensual + MTTR.'
    }
  ];

  const StatCard = ({ icon: Icon, title, value, subtitle, color, trend, info }) => (
    <Card sx={{ 
      height: '100%',
      background: themeColors.cardBg,
      borderRadius: 3,
      border: `1px solid ${themeColors.cardBorder}`,
      boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)'
      }
    }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ 
            width: 42, 
            height: 42, 
            borderRadius: 2.5,
            background: isDark ? `${color}30` : `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon sx={{ color: color, fontSize: 22 }} />
          </Box>
          {info && (
            <Tooltip title={info} placement="top" arrow>
              <IconButton size="small" sx={{ color: themeColors.textSecondary, '&:hover': { color: themeColors.textPrimary } }}>
                <HelpOutline sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: themeColors.textPrimary, fontSize: '1.75rem', mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: themeColors.textSecondary, fontSize: '0.8rem', mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {trend === 'up' && <ArrowUpward sx={{ fontSize: 14, color: colors.success }} />}
            {trend === 'down' && <ArrowDownward sx={{ fontSize: 14, color: colors.error }} />}
            <Typography variant="caption" sx={{ 
              color: trend === 'up' ? colors.success : trend === 'down' ? colors.error : themeColors.textSecondary,
              fontWeight: 500
            }}>
              {subtitle}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: themeColors.textPrimary, fontSize: '1.75rem' }}>
            Dashboard
          </Typography>
          <Chip 
            label="Caldea Club × PEpL" 
            size="small"
            sx={{ 
              bgcolor: themeColors.chipBg, 
              color: colors.primary,
              fontWeight: 600,
              fontSize: '0.7rem'
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
          Recovery Rewards & Wellness Challenges — Febrero 2026
        </Typography>
      </Box>

      {/* KPIs Principales */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            icon={People}
            title="Usuarios Activos"
            value={caldeaMetrics.activeUsers.toLocaleString()}
            subtitle="↑ 12% vs dic"
            color={colors.primary}
            trend="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            icon={Repeat}
            title="Frecuencia/Sem"
            value={caldeaMetrics.weeklyVisits}
            subtitle="↑ 0.4 vs baseline"
            color={colors.success}
            trend="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            icon={Spa}
            title="Canjes Hoy"
            value={caldeaMetrics.rewardsRedeemed}
            subtitle="Recovery + Wellness"
            color="#a855f7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            icon={TrendingUp}
            title="Retención 30d"
            value={`${caldeaMetrics.retentionRate}%`}
            subtitle="↑ 22pp con PEpL"
            color={colors.warning}
            trend="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            icon={EmojiEvents}
            title="En Challenges"
            value={caldeaMetrics.challengeParticipants}
            subtitle="4 activos"
            color="#eab308"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            icon={LocalDrink}
            title="Secondary Spend"
            value="€12.4"
            subtitle="promedio post-reward"
            color="#ec4899"
          />
        </Grid>
      </Grid>

      {/* KPIs BI powered by PEpL */}
      <Accordion 
        defaultExpanded={false} 
        sx={{ 
          mb: 3, 
          borderRadius: 3, 
          border: `1px solid ${themeColors.cardBorder}`,
          boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
          '&:before': { display: 'none' },
          overflow: 'hidden',
          bgcolor: themeColors.cardBg
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore sx={{ color: themeColors.textSecondary }} />}
          sx={{ 
            bgcolor: themeColors.cardBg,
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Insights sx={{ color: colors.primary, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 600, color: themeColors.textPrimary }}>
              KPIs BI
            </Typography>
            <Chip 
              label="powered by PEpL" 
              size="small"
              sx={{ 
                bgcolor: themeColors.chipBg, 
                color: colors.primary,
                fontWeight: 500,
                fontSize: '0.65rem',
                height: 20
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: themeColors.tableBg, pt: 2 }}>
          <Grid container spacing={2}>
            {biKpis.map((kpi) => (
              <Grid item xs={12} sm={6} md={3} key={kpi.title}>
                <StatCard
                  icon={kpi.icon}
                  title={kpi.title}
                  value={kpi.value}
                  subtitle={kpi.subtitle}
                  color={kpi.color}
                  trend={kpi.trend}
                  info={kpi.info}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Charts Row 1 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            borderRadius: 3, 
            border: `1px solid ${themeColors.cardBorder}`,
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
            bgcolor: themeColors.cardBg
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: themeColors.textPrimary, fontSize: '1rem' }}>
                Canjes vs Sesiones de Entrenamiento
              </Typography>
              <Typography variant="caption" sx={{ color: themeColors.textSecondary, display: 'block', mb: 2 }}>
                Última semana
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={dailyRedemptions}>
                  <defs>
                    <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={colors.primary} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorCanjes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.success} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={colors.success} stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: themeColors.textSecondary, fontSize: 12 }} />
                  <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: themeColors.textSecondary, fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: themeColors.textSecondary, fontSize: 12 }} />
                  <RechartsTooltip 
                    contentStyle={{ 
                      borderRadius: 8, 
                      border: 'none', 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      backgroundColor: themeColors.cardBg,
                      color: themeColors.textPrimary
                    }} 
                  />
                  <Legend wrapperStyle={{ paddingTop: 16 }} />
                  <Area yAxisId="left" type="monotone" dataKey="sessions" stroke={colors.primary} strokeWidth={2} fillOpacity={1} fill="url(#colorSessions)" name="Sesiones entreno" />
                  <Area yAxisId="right" type="monotone" dataKey="canjes" stroke={colors.success} strokeWidth={2} fillOpacity={1} fill="url(#colorCanjes)" name="Canjes rewards" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3, 
            border: `1px solid ${themeColors.cardBorder}`,
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
            bgcolor: themeColors.cardBg
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: themeColors.textPrimary, fontSize: '1rem' }}>
                Distribución de Canjes
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={rewardsByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {rewardsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2 }}>
                {rewardsByType.map((item) => (
                  <Chip 
                    key={item.name}
                    label={item.name} 
                    size="small" 
                    sx={{ 
                      backgroundColor: `${item.color}15`, 
                      color: item.color, 
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ 
            borderRadius: 3, 
            border: `1px solid ${themeColors.cardBorder}`,
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
            bgcolor: themeColors.cardBg
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: themeColors.textPrimary, fontSize: '1rem' }}>
                Retención: PEpL vs Control
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={retentionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                  <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: themeColors.textSecondary, fontSize: 12 }} />
                  <YAxis dataKey="cohort" type="category" width={80} axisLine={false} tickLine={false} tick={{ fill: themeColors.textSecondary, fontSize: 12 }} />
                  <RechartsTooltip 
                    formatter={(value) => `${value}%`}
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: themeColors.cardBg, color: themeColors.textPrimary }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 8 }} />
                  <Bar dataKey="d7" fill={colors.success} name="Día 7" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="d14" fill={colors.primary} name="Día 14" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="d30" fill="#a855f7" name="Día 30" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2, p: 2, background: isDark ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.success, fontSize: '0.8rem' }}>
                  💡 +22 pp de retención a 30 días con PEpL activo
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card sx={{ 
            borderRadius: 3, 
            border: `1px solid ${themeColors.cardBorder}`,
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
            bgcolor: themeColors.cardBg
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: themeColors.textPrimary, fontSize: '1rem' }}>
                  Actividad en Tiempo Real
                </Typography>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.success, animation: 'pulse 2s infinite' }} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: themeColors.textSecondary, fontSize: '0.75rem', borderBottom: `1px solid ${themeColors.cardBorder}`, bgcolor: themeColors.tableBg }}>Usuario</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: themeColors.textSecondary, fontSize: '0.75rem', borderBottom: `1px solid ${themeColors.cardBorder}`, bgcolor: themeColors.tableBg }}>Acción</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: themeColors.textSecondary, fontSize: '0.75rem', borderBottom: `1px solid ${themeColors.cardBorder}`, bgcolor: themeColors.tableBg }}>Trigger</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: themeColors.textSecondary, fontSize: '0.75rem', borderBottom: `1px solid ${themeColors.cardBorder}`, bgcolor: themeColors.tableBg }}>Tiempo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivity.map((activity, index) => (
                      <TableRow key={index} hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' } }}>
                        <TableCell sx={{ borderBottom: `1px solid ${themeColors.cardBorder}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: colors.primary, fontWeight: 600 }}>
                              {activity.user.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', color: themeColors.textPrimary }}>{activity.user}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${themeColors.cardBorder}`, fontSize: '0.8rem', color: themeColors.textPrimary }}>{activity.action}</TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${themeColors.cardBorder}` }}>
                          <Chip 
                            label={activity.trigger} 
                            size="small" 
                            sx={{ 
                              bgcolor: themeColors.chipBg, 
                              color: colors.primary,
                              fontWeight: 500,
                              fontSize: '0.7rem',
                              height: 24
                            }} 
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${themeColors.cardBorder}` }}>
                          <Typography variant="caption" sx={{ color: themeColors.textSecondary, fontSize: '0.75rem' }}>{activity.time}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
